import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { RELATION_COLORS, RELATION_RGB, TEAM_COLORS } from "src/utils/teamColors"

import UnderConstruction from "src/components/UnderConstruction"
import { unitIcons } from "src/assets/images"
import { drawEdges, drawGround, drawHover } from "src/utils/terrain"
import { activeWorld } from "src/utils/world"
import { showTimeAt, stateAt } from "src/utils/roundMotion"
import { EFFECT_FRAMES, drawEffect, effectFrameId, motionImages } from "src/utils/mapSprites"
import { battleFor, drawBattle, drawFormation, formationFor } from "src/utils/formation"

// The frame of the cell the player picked, and of the route being drawn (owner, 2026-10-26).
const SELECTED_FRAME = "rgba(255, 255, 0, 0.95)"
const minSquareSize = 12
// The closest the camera goes: 192 px a cell (owner, 2026-10-26: "let me zoom a little bit more"; it was 128).
const maxSquareSize = 192
const initialSquareSize = 24
const initialBorderWidth = 0
// How see-through a march line is (owner, 2026-10-26: the prototype's lines).
const ROUTE_ALPHA = 0.55
const coordinatesPadding = 5
const millisecondsToConsiderHovered = 350
/* The world's size is the active world's (`utils/world`, requirement 35: S, M or L), read at
   call time. The Game and Lobby views key this component by the size, so a change of world is a
   fresh canvas rather than a camera left where the last world put it. */
const worldCols = () => activeWorld().columns
const worldRows = () => activeWorld().rows

/* The world wraps east to west (owner's ask, 2026-09-05): the last column stands beside the first,
   as the Pacific does on a real map, and the canvas draws the world as a cylinder — scroll right
   past column 199 and column 0 follows, with no edge. Rows do not wrap: past the top and the
   bottom there is space, painted black with a few stars, and no cell. The server measures every
   distance the same way (`simulator/domain/world_data.distance`), so a march may step from 199 to
   0 and the route builder in utils/maps takes the short way round. */
export const wrapCol = (col) => {
  const maxCol = worldCols()
  return ((col % maxCol) + maxCol) % maxCol
}

/* A key with a possibly negative or over-range column, from the unwrapped node lists below.
   `"-1-5".split("-")` is why this exists. */
const parseKey = (key) => {
  const match = /^(-?\d+)-(-?\d+)$/.exec(key)
  return match ? [Number(match[1]), Number(match[2])] : key.split("-").map(Number)
}

/* Unwrap a list of "col-row" keys so consecutive steps never jump across the seam: a route that
   walks 198, 199, 0, 1 becomes 198, 199, 200, 201, and can be drawn as one line. The first key
   anchors the tile. */
export const unwrapKeys = (keys) => {
  if (!keys || keys.length === 0) return []
  const maxCol = worldCols()
  let [prevCol] = parseKey(keys[0])
  let shift = 0
  return keys.map((key, index) => {
    const [col, row] = parseKey(key)
    if (index > 0) {
      const delta = col - prevCol
      if (delta > maxCol / 2) shift -= maxCol
      else if (delta < -maxCol / 2) shift += maxCol
    }
    prevCol = col
    return `${col + shift}-${row}`
  })
}

/* A star or not, for a cell of space: a small deterministic hash so the sky is the same every
   frame and every scroll, rather than a fresh scatter that shimmers. About one cell in twenty
   holds a star, one in a hundred a bright one. */
const starAt = (col, row) => {
  let h = (col * 73856093) ^ (row * 19349663)
  h = (h ^ (h >>> 13)) * 1274126177
  h = (h ^ (h >>> 16)) >>> 0
  const roll = h % 100
  if (roll >= 5) return null
  return { fx: ((h >>> 8) % 100) / 100, fy: ((h >>> 16) % 100) / 100, bright: roll === 0 }
}

// Global variables in the page. Take into account that this wouldn't support
// multiple canvases in the same page. If you need that, you should use "useRef"
let positionOnMouseDown = [-999, -999]
let isDragging = false

/* --- Touch ---------------------------------------------------------------------------------
   The map was mouse-only: `wheel` to scroll, `mousedown`+`mousemove` to drag, and a click that
   demanded the pointer land on the exact pixel it went down on. A finger satisfies none of that.
   Compatibility mouse events cover a clean tap and nothing else — they are not streamed during a
   touch drag — so panning and zooming simply did not exist on a phone, and the canvas's own
   `touch-action: none` had already switched off the browser's versions of both.

   Three gestures, chosen because they are what the mouse already does: drag to pan, pinch to
   zoom, and press-and-hold where the mouse hovers. Everything below is expressed in the same
   offset/squareSize terms the mouse handlers use, so there is one camera and not two.

   A tap tolerates movement, because a finger always moves a little. That tolerance now applies to
   the mouse too: `handleMouseUp` compared coordinates for equality, so a mouse that crept one
   pixel between press and release selected nothing and gave no sign why. */
const TAP_SLOP_PX = 12
const LONG_PRESS_MS = 450
// Below this the two touches are close enough that the ratio is mostly noise, and a pinch that
// starts at a near-zero distance scales to the limit on the first frame.
const MIN_PINCH_SPAN_PX = 24

export const touchSpan = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
export const touchMidpoint = (a, b) => [(a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2]
export const movedBeyondSlop = (x1, y1, x2, y2) => Math.hypot(x1 - x2, y1 - y2) > TAP_SLOP_PX

/* Where a pinch leaves the camera, as a pure function of the gesture and the fingers' current
   span and midpoint — separated out because it is the one piece of this that is arithmetic rather
   than plumbing, and arithmetic that is wrong by a ratio looks exactly like arithmetic that is
   right until somebody pinches near an edge.

   `anchorCol`/`anchorRow` are the world point the fingers opened on. Keeping it under the
   midpoint is the whole behaviour: zoom about the pinch, and pan with it when both fingers
   travel. The mouse wheel runs through here too (a one-axis pinch about the pointer), and the
   phone's +/- pair obeys the same clamp, so no zoom can reach a size another cannot. */
export const pinchTo = ({ gesture, span, bufferX, bufferY }) => {
  const squareSize = Math.max(
    minSquareSize,
    Math.min(maxSquareSize, (gesture.startSquareSize * span) / gesture.startSpan)
  )
  return {
    squareSize,
    offsetX: gesture.anchorCol * squareSize - bufferX,
    offsetY: gesture.anchorRow * squareSize - bufferY,
  }
}

/* --- The flight (SV12) -----------------------------------------------------------------------

   `scrollTo` puts the camera somewhere in one step. A wave of survival bots needs the other
   thing: the map *travelling* to each newcomer, so a player watching sees where on the world it
   landed rather than being teleported and left to work out what moved.

   A camera here is `{col, row, squareSize}` — the world point at the centre of the view and how
   big a cell is — which is the same pair `pinchTo` speaks in and the same pair the `onMoveHooks`
   report, so there is one camera and a flight can end exactly where another began. Cell
   *coordinates*, not percentages: the tour centres a crown, and "centre the cell at 103-16" has
   to mean the same thing at every zoom.

   Pure, for the reason `pinchTo` is: it is the arithmetic, and arithmetic that eases wrongly
   looks right until it crosses the seam. */
export const FLIGHT_MS = 700

// Fast out of the old place, slow into the new one — the shape every "take me there" in this app
// uses, and the one that reads as travel rather than as a jump with a blur on it.
export const easeOutCubic = (progress) => 1 - (1 - progress) ** 3

/* The cells of the line a stack eats as it walks (requirement 36, M3): from where it stands now
   (fractional), along the cells the round still has it walk, then on along the board's road ahead
   (`road.nodes`, `"<col>-<row>"`), and the direction it ends in (`dx`, `dy`, one of them 0).
   `null` for a line of one cell. */
export const eatenLine = (sprite, road, maxCol = worldCols()) => {
  // A whole number of worlds: `wrap(col) - col` on a fractional column is off by a rounding error,
  // and the cells ahead (whole numbers) took it on while the road's did not, so the arrow's
  // direction read the error's sign and swung from side to side every frame (owner, 2026-10-26,
  // game 4 round 6).
  const wrapped = ((sprite.col % maxCol) + maxCol) % maxCol
  const shift = Math.round((wrapped - sprite.col) / maxCol) * maxCol
  const cells = [[sprite.col, sprite.row], ...(sprite.ahead || [])].map(([col, row]) => [col + shift, row])
  if (road && (road.nodes || []).length > 1) {
    const roadCells = road.nodes.map(parseKey)
    const last = cells[cells.length - 1]
    const lift = Math.round((last[0] - roadCells[0][0]) / maxCol) * maxCol
    roadCells.slice(1).forEach(([col, row]) => cells.push([col + lift, row]))
  }
  if (cells.length < 2) return null
  const [endCol, endRow] = cells[cells.length - 1]
  const [prevCol, prevRow] = cells[cells.length - 2]
  // The stack itself may be the cell before the last, a fraction of a cell away.
  const sign = (d) => (Math.abs(d) < 1e-6 ? 0 : Math.sign(d))
  const dx = sign(endCol - prevCol)
  const dy = dx === 0 ? sign(endRow - prevRow) : 0
  return { cells, dx, dy }
}

export const flightFrame = ({ from, to, progress, columns = worldCols() }) => {
  const t = easeOutCubic(Math.max(0, Math.min(1, progress)))
  /* East-west the world is a cylinder, so the flight takes the short way round — the same rule
     the routes, the distances and the camera's own wrap keep. Without it a hop from column 198 to
     column 1 is a three-cell trip drawn as a 197-cell one, across every other player's empire. */
  let columnsToGo = to.col - from.col
  const half = columns / 2
  if (columnsToGo > half) columnsToGo -= columns
  else if (columnsToGo < -half) columnsToGo += columns
  return {
    col: from.col + columnsToGo * t,
    row: from.row + (to.row - from.row) * t,
    squareSize: from.squareSize + (to.squareSize - from.squareSize) * t,
  }
}

// A flight is motion for its own sake, which is exactly what this switch is asked for: the camera
// still arrives, in one frame, and the tour's dwell still runs. Read at the moment of the flight
// rather than cached, since a player may change it mid-game.
export const asksForStillness = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

/* How much one wheel event scales the map, as a pinch's span ratio. Exponential in the delta, so
   a notch in and a notch out land back where they started, and a trackpad's stream of small
   deltas adds up to the same zoom as one mouse notch of their sum. A trackpad *pinch* arrives as
   a wheel event with `ctrlKey` and deltas an order of magnitude smaller, hence its own gain.
   Line-mode deltas (Firefox's mouse wheel) are converted to pixels first. */
const WHEEL_ZOOM_GAIN = 0.0015
const WHEEL_PINCH_GAIN = 0.01
const WHEEL_LINE_PX = 16

export const wheelZoomFactor = ({ deltaY, deltaMode = 0, ctrlKey = false }) => {
  const pixels = deltaMode === 1 ? deltaY * WHEEL_LINE_PX : deltaY
  return Math.exp(-pixels * (ctrlKey ? WHEEL_PINCH_GAIN : WHEEL_ZOOM_GAIN))
}

// Also `--crown` in theme.css, which is what the roster row's border uses. A canvas cannot read a
// custom property, so the value is written twice; they are one colour and must move together.
const CROWN_GOLD = "#f2c14e"
const CROWN_GOLD_DARK = "rgba(92, 62, 6, 0.95)"

/* The crown glyph, drawn rather than set as the 👑 emoji. The emoji was the first cut and it was
   the wrong tool for a grid cell: a font glyph carries its own metrics, so at map sizes it spilled
   over the coordinates label and the character sprite with no way to trim it. A path has exactly
   the bounds we give it. (cx, cy) is its centre. */
const traceCrown = (ctx, cx, cy, w) => {
  const h = w * 0.78
  const left = cx - w / 2
  const top = cy - h / 2
  ctx.beginPath()
  ctx.moveTo(left, top + h)
  ctx.lineTo(left + w, top + h)
  ctx.lineTo(left + w, top + h * 0.3)
  ctx.lineTo(left + w * 0.72, top + h * 0.55)
  ctx.lineTo(left + w * 0.5, top)
  ctx.lineTo(left + w * 0.28, top + h * 0.55)
  ctx.lineTo(left, top + h * 0.3)
  ctx.closePath()
}

/* How a crown city is marked on the map: a gold crown on a dark chip in the **bottom-right**
   corner. The corner is the whole point — the cell's other two tenants are the coordinates label
   (top-left) and the sprite (centre), and the bottom-right is the one quadrant neither claims, so
   the badge never sits on top of either. Faded while the crown's player has lost the cell.

   Below ~18px a cell has no room for a disc, so the chip is dropped and the silhouette drawn a
   little larger instead: zoomed out, a gold speck in the corner is still the only thing that has
   to read, and a chip at that size would just swallow the cell. */
const drawCrownMark = (ctx, x, y, squareSize, held) => {
  const size = Math.max(7, squareSize * 0.3)
  const cx = x + squareSize - size / 2 - 1.5
  const cy = y + squareSize - size / 2 - 1.5
  const onAChip = squareSize >= 18

  ctx.save()
  ctx.globalAlpha = held ? 1 : 0.45

  if (onAChip) {
    ctx.beginPath()
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2)
    ctx.fillStyle = "rgba(24, 18, 4, 0.78)"
    ctx.fill()
    ctx.lineWidth = 1
    ctx.strokeStyle = held ? CROWN_GOLD : "rgba(242, 193, 78, 0.7)"
    ctx.stroke()
  }

  traceCrown(ctx, cx, cy, size * (onAChip ? 0.6 : 0.95))
  ctx.fillStyle = CROWN_GOLD
  ctx.fill()
  ctx.lineWidth = onAChip ? 0.75 : 1
  ctx.strokeStyle = CROWN_GOLD_DARK
  ctx.stroke()

  ctx.restore()
}

const getInitialPosition = (initialPosition) => {
  const [canvasWidth, canvasHeight] = getCanvasDimensions()
  const newOffsetX = initialPosition[0] * initialSquareSize - canvasWidth / 2
  const newOffsetY = initialPosition[1] * initialSquareSize - canvasHeight / 2
  return [newOffsetX, newOffsetY]
}

const getCanvasDimensions = () => {
  const paddingCorrectionOnCenterBlock = 0.02
  return [window.innerWidth * (0.5 - paddingCorrectionOnCenterBlock), window.innerHeight]
}

// The box the map was laid into, for callers that place it in a sized container (see `fitToParent`).
// The canvas stretches to that box in CSS, so measuring the parent — not the canvas — is what keeps
// the drawing buffer and the displayed size the same and the cells square.
const getParentDimensions = (canvas) => {
  const parent = canvas?.parentElement
  const rect = parent ? parent.getBoundingClientRect() : null
  if (!rect || rect.width === 0 || rect.height === 0) return getCanvasDimensions()
  return [rect.width, rect.height]
}

/* The drawing buffer is sized in *device* pixels and everything else in CSS pixels.

   It used to be one and the same: `canvas.width` was set to the box's CSS width, so on a phone at
   devicePixelRatio 3 the map was drawn at a third of the screen's resolution and upscaled — soft
   sprites, soft grid, soft coordinate labels, on exactly the devices whose screens are sharpest.
   (MiniMap.js has always done this correctly; this is that, applied to the map itself.)

   The bargain is that the buffer is now `ratio` times larger while offsets, `squareSize` and every
   world coordinate stay in CSS pixels — `draw` scales the context once so nothing else has to
   think about it. What does have to think about it is anything that read `canvas.width` as "how
   wide is the view", which is now `viewWidth`. Reading the raw buffer instead would report a
   viewport three times too wide on a phone: too many columns drawn, the wrong centre on every
   zoom, and a scroll limit that lets the map run off the edge.

   The ratio is stashed on the element because `resizeCanvas` is the only place that knows it and
   the readers only ever hold the canvas. A canvas that has never been through it — a plain object
   in a test, a first render — answers 1, which is exactly what it was before. */
const deviceRatio = () => (typeof window !== "undefined" && window.devicePixelRatio) || 1

export const viewScale = (canvas) => canvas?.viewScale || 1
export const viewWidth = (canvas) => canvas.width / viewScale(canvas)
export const viewHeight = (canvas) => canvas.height / viewScale(canvas)

/* The centre of the view in world cells and the cell size, read off the canvas as it is now. */
export const stashCamera = (canvas, view) => {
  if (!canvas) return
  canvas.view = view
  if (Object.getOwnPropertyDescriptor(canvas, "camera")?.get) return
  Object.defineProperty(canvas, "camera", {
    configurable: true,
    get: () => {
      const { offsetX, offsetY, squareSize } = canvas.view
      return {
        col: (offsetX + viewWidth(canvas) / 2) / squareSize,
        row: (offsetY + viewHeight(canvas) / 2) / squareSize,
        squareSize,
      }
    },
  })
}

/* Which cell a pointer is over.

   The canvas is stretched to its box by CSS (`.lobby-map canvas`, `.game-map`), while its drawing
   buffer is sized separately in `resizeCanvas`. Nothing forces those two to agree: the buffer is
   set on mount and on window resize, and the lobby's map box also changes when the panel *below*
   it does — picking a side, picking a class, a player joining. From then on one CSS pixel is not
   one buffer pixel, and everything drawn is in buffer pixels.

   Both handlers used to subtract the box's origin and divide, which is only right at a scale of
   exactly 1. Off it, the reported cell drifts further the further from the top-left corner you
   click — which is the bug this exists to kill: you press a cell and a different one lights up.

   Scaling here rather than only keeping the two sizes in step is deliberate. The sync (see the
   ResizeObserver below) is what keeps the map sharp; this is what keeps the *answer* right, and it
   stays right for one frame after any resize, before the observer has run. */
export const cellFromPointer = ({ canvas, clientX, clientY, offsetX, offsetY, squareSize }) => {
  const rect = canvas.getBoundingClientRect()
  const scaleX = rect.width ? viewWidth(canvas) / rect.width : 1
  const scaleY = rect.height ? viewHeight(canvas) / rect.height : 1

  const worldX = offsetX + (clientX - rect.left) * scaleX
  const worldY = offsetY + (clientY - rect.top) * scaleY

  // The column wraps: a click on the tile drawn past the seam is a click on the real cell.
  return { col: wrapCol(Math.floor(worldX / squareSize)), row: Math.floor(worldY / squareSize) }
}

/* The camera's limits: the world's own edges (owner's ask, 2026-09-04).

   The guard this replaces asked only whether *any* of the world was still on screen — so a drag
   could park 199 of the 200 columns off the left edge and fill the view with void, which reads as
   the map having broken rather than as the map having ended.

   Two changes, and the second is the one that matters for the feel. It clamps to the world's edges
   instead of to a sliver of it; and it *clamps* rather than *refuses*, so a drag that would go past
   the edge slides up to it and stops, where the old guard threw the whole move away and the map
   stuck fast a gesture before the corner.

   Zoomed out far enough that the world no longer fills the view, there is no range to clamp into —
   the world is centred instead, which is the only position that is not an arbitrary corner.

   With half a view of give past each edge (owner's ask, 2026-09-05): a city on the map's border
   could never be put in the middle of the screen, because the edge stopped dead at the view's
   edge — so a border city was always read from a corner, with its panels over it. Half the view
   is exactly the give that lets the world's edge reach the centre and no further; past the edge
   the canvas paints void, never water, and no pointer resolves to a cell there. */
export const OVERSCROLL_FRACTION = 0.5
export const clampOffset = (newOffsetX, newOffsetY, canvas, squareSize) => {
  const clamp = (value, span, world) => {
    if (world <= span) return (world - span) / 2
    const give = span * OVERSCROLL_FRACTION
    return Math.min(Math.max(value, -give), world - span + give)
  }
  // East-west the camera is never clamped, only normalised: the world is a cylinder and the
  // offset is kept in [0, worldWidth) so every reader sees one canonical position.
  const worldWidth = worldCols() * squareSize
  const wrappedX = ((newOffsetX % worldWidth) + worldWidth) % worldWidth
  return [wrappedX, clamp(newOffsetY, viewHeight(canvas), worldRows() * squareSize)]
}

/* The world is `worldRows()` rows tall, numbered 0..rows-1 — the same bound the server's
   `is_in_bounds` checks — and every column is on the map, because columns wrap. Every pointer
   path asks this before it hands a cell to a hook: above and below the map is space, and space
   is nowhere. */
export const isOnMap = (col, row) => Number.isInteger(col) && row >= 0 && row < worldRows()

/* Where a leg between two cells leaves the first and enters the second (owner's S3, 2026-09-05):
   the midpoint of the edge each cell turns towards the other, so the line clears both cells'
   contents — centre to centre it ran over the sprite it was pointing at. `from` and `to` are
   [col, row] pairs (unwrapped, see `unwrapKeys`); the answer is canvas pixels. `inset` pulls
   both feet back into their own cell by that fraction of a cell: two neighbours' facing edges
   are one and the same line, and the hop between them needs a chord to bend over. */
export const legEndpoints = ({ from, to, squareSize, offsetX = 0, offsetY = 0, inset = 0 }) => {
  const [c1, r1] = from
  const [c2, r2] = to
  const dx = Math.sign(c2 - c1)
  const dy = dx === 0 ? Math.sign(r2 - r1) : 0
  const centre = (col, row) => [
    col * squareSize - offsetX + squareSize / 2,
    row * squareSize - offsetY + squareSize / 2,
  ]
  const [ax, ay] = centre(c1, r1)
  const [bx, by] = centre(c2, r2)
  const reach = squareSize * (0.5 - inset)
  return {
    start: [ax + dx * reach, ay + dy * reach],
    end: [bx - dx * reach, by - dy * reach],
    direction: [dx, dy],
  }
}

/* Where a one-cell hop stands (owner's S3, 2026-09-05). Two neighbours share the edge that
   faces the other, so a hop cannot leave from it — the chord would be nothing. It leaves from the
   edge *beside* the border instead: the top edges of both cells for a sideways hop, the left edges
   for a vertical one. The chord is then a whole cell wide, the half-circle bulges away from both
   cells' contents, and the arrowhead drops into the target from that side. `from` and `to` are
   [col, row] (unwrapped); the answer is canvas pixels plus the arc's outward direction. */
export const hopFeet = ({ from, to, squareSize, offsetX = 0, offsetY = 0 }) => {
  const [c1, r1] = from
  const [c2, r2] = to
  const sideways = r1 === r2
  const centre = (col, row) => [
    col * squareSize - offsetX + squareSize / 2,
    row * squareSize - offsetY + squareSize / 2,
  ]
  const [ax, ay] = centre(c1, r1)
  const [bx, by] = centre(c2, r2)
  const half = squareSize / 2
  return sideways
    ? { start: [ax, ay - half], end: [bx, by - half], outward: [0, -1] }
    : { start: [ax - half, ay], end: [bx - half, by], outward: [-1, 0] }
}

/* The unit-sprite outline is baked into the icon PNGs, not drawn at runtime (owner, 2026-09-05):
   the colour icons carry a dark rim in the asset itself, and the black ones (the default zombie,
   the skull / jaws / grave looks) carry none — "the modification should be done at png level".
   So the canvas just draws the sprite as it is, on the map and in the shop alike. */

const convertOffsetCoordinatesToPercentage = (offsetX, offsetY, squareSize) => {
  const percentX = Math.max(Math.min((100 * offsetX) / (worldCols() * squareSize), 100), 0)
  const percentY = Math.max(Math.min((100 * offsetY) / (worldRows() * squareSize), 100), 0)
  return [percentX, percentY]
}

const Canvas = ({
  initialPosition,
  scrollTo,
  flyTo = null,
  elements = {},
  movements = [],
  onMoveHooks = [],
  onClickHooks = [],
  onHoverHooks = [],
  onHoverEndHooks = [],
  focusRange = null,
  seenKeys = null,
  reach = null,
  showGrid = true,
  fitToParent = false,
  motion = null,
  onCancelDischarge = null,
}) => {
  const { t } = useTranslation()
  const cancelDischargeLabel = t("city.cancelDischargeHint")
  /*
  initialPosition: Initial position of the canvas. It should be an array with two numbers, the first one being the
  x-coordinate and the second one being the y-coordinate. The canvas will be centered on this position.

  scrollTo: To scroll to another position, given an array of percentages [x, y], the canvas will scroll to that position.

  flyTo: {col, row, squareSize, seq} — the same move, *travelled* rather than jumped (SV12). The
    camera tweens to "that world point at the centre of the view, at that cell size" over
    FLIGHT_MS with an ease-out, on requestAnimationFrame; `squareSize` is optional and defaults to
    the one the map is at, and is clamped to the same limits every zoom obeys. `seq` is what fires
    it, exactly as `pingCity`'s is: flying to the same cell twice is two flights, and a re-render
    that hands back the same object is none. `col`/`row` are world coordinates in *cells* and may
    be fractional — a cell is centred by passing its column and row plus a half — which is also
    the shape the onMoveHooks report, so a flight can put the camera back where it found it.
    Under `prefers-reduced-motion` the camera arrives in one frame.

  elements: Pass an object that contains on each cell what elements are there. The object should be a dictionary with the key "{column}-{row}", containing an array of objects with the following properties:"
    + type: "human", "zombie", "highlight", "outline", "combat", "crown", "camp", "selected", "strike", "founding", "tunnel", "stack", "discharge"
    A "stack" element is troops standing on ground nobody owns (requirement 36, S3): the top-down
    formation of their composition (`units`, drawn by `utils/formation`), standing; `team` picks the
    side's figures, and a headcount with no composition draws the side's generic ones.
    A "strike" element marks a missile in the air: a crosshair on the square it lands on.
    A "tunnel" element marks a tunnel order of this round (`direction`: "load" | "unload").
    A "founding" element marks a colony ordered this round: a swinging pick in the bottom-left
    corner, laid over the canvas, founded when the round turns.
    A "discharge" element marks a discharge ordered this round: the same pick; with `mine` true
    and an `onCancelDischarge` prop it is a button that takes the order back ({col, row}).
    A "camp" element marks units standing on a cell somebody else owns — an ally garrisoned in
    your city, an enemy's partner in theirs, your own expedition on open ground. `color` is the
    tent's fill and is chosen by relation to the viewer; `team` is the fallback.
    + player: X
    A "crown" element marks a victory-deciding city: a gold crown badge in the cell's
    bottom-right corner, faded while `held` is false (its player has lost the cell).

  movements:
  - id: int
  - type: ally_human/ally_zombie/enemy/self
  - action: "attack/move"
  - nodes: ["29-14", "30-14"]
  - currentPosition: "29-14"

  onMoveHooks: Pass functions that will be executed everytime the position of the camera on map changes (or the size of the squares).
    Each receives the position as map percentages [x, y] — what the minimap draws — and, second,
    the camera itself: {col, row, squareSize}, the world point at the centre of the view in cells.
    That second argument is exactly what `flyTo` takes, which is what lets a caller remember where
    the player was and fly them back to it.

  onClickHooks: Pass functions that will be executed everytime the user clicks on a cell in the map. The function will receive an object with the row and column of the cell clicked.

  onHoverHooks: Pass functions that will be executed once the pointer has rested on a cell for a moment. They receive
    { col, row, clientX, clientY } — the pointer position too, so a tooltip can sit beside it.
  onHoverEndHooks: Run when the pointer leaves that cell (moves to another, drags, or leaves the map).
  focusRange: {col, row, radius} — everything farther than `radius` (Manhattan, the server's metric) from
    that cell is washed darker, water included, so a city's detection range reads on the map.
  seenKeys: a Set of "col-row" keys the wash leaves alone — the squares the viewer's team already
    looks over (U3), so the ring shows what the pressed cell adds to that rather than standing alone.
  reach: {rounds: Map<"col-row", number>} — while a route is drawn, the cells the stack reaches in
    ONE round from the end of the route (`utils/movementEta.reachForStack`, searched to one round).
    They get an amber tint and an amber line round their edge; nothing else is shaded (owner,
    2026-09-29: a shade per round read as a fog over the map).
  showGrid: false hides the cell borders and the coordinate labels (the eye by the minimap). The
    zoomed-out rule still applies on top: coordinates never draw below the size they are legible at.

  fitToParent: Size the drawing buffer to the parent element instead of to half the window. The
  default sizing assumes the old three-column layout, where the map owned the middle of the screen;
  the lobby draws it into a grid cell that is neither half the width nor the full height. Only pass
  this when the parent has a size of its own (a grid/flex track), or it has nothing to measure.

  motion: {timeline, startedAt, base, speed} while the round that just turned plays itself
    (requirement 36, M5), null the rest of the time. `timeline` is `utils/roundMotion.buildTimeline`'s;
    the moment of it on screen is `roundMotion.showTimeAt` (M10: `base` ms in at `startedAt`, played
    at `speed`). While set, the map redraws every frame on
    requestAnimationFrame — the flight's machinery — and draws each stack of the log at its
    animated position (fractional cells, flipped when it faces west) and each effect from its sheet
    by progress, over the marches and under nothing. The camera never moves. The caller takes the
    live board's copies of those stacks off `elements` (`buildBoard`'s `motion`); this draws only
    what the timeline says. Ignored under `prefers-reduced-motion`: the live board lands at once.
  */

  const canvasRef = useRef(null)
  // Every map sprite a player might be wearing, not one per team. Which one a cell gets is the
  // cell owner's choice and arrives on the element as `skin`; the whole set is preloaded because
  // it is eight small bundled files and `styleCanvas` draws once they have all landed — loading
  // them as they are first needed would mean a first frame with holes in it.
  // The effect sheets ride along with them (requirement 36): drawn on the same canvas, and
  // whichever of them the tree does not have yet is drawn in vector by `utils/mapSprites`, so a
  // missing file never holds the first frame. A stack is no picture: it is drawn in vector too
  // (`utils/formation`).
  const allImages = useRef(
    Object.fromEntries(
      Object.entries({ ...unitIcons, ...motionImages() }).map(([id, src]) => [id, [new Image(), src, false]])
    )
  )

  const [ox, oy] = getInitialPosition(initialPosition)
  const [offsetX, setOffsetX] = useState(ox)
  const [offsetY, setOffsetY] = useState(oy)
  const [squareSize, setSquareSize] = useState(initialSquareSize)
  const [borderWidth, setBorderWidth] = useState(initialBorderWidth)
  const [startX, setStartX] = useState(0)
  const [startY, setStartY] = useState(0)
  const [lastOffsetX, setLastOffsetX] = useState(0)
  const [lastOffsetY, setLastOffsetY] = useState(0)

  // Bumped when the box changes size, purely to get one more render — and with it the redraw the
  // resized (and therefore cleared) buffer needs.
  const [, setResizes] = useState(0)
  const [previousScrollTo, setPreviousScrollTo] = useState(scrollTo)
  const hoverTimerRef = useRef(null)
  const lastHoveredCellRef = useRef(null)
  // The cell under the pointer, for the frame that stands in for the grid (the ground has
  // none). Set on the move, not after the hover delay: the frame has to follow the mouse.
  const [hoveredCell, setHoveredCell] = useState(null)
  /* The live gesture, in a ref rather than the module-level pair the mouse uses: a pinch spans
     several renders (every frame sets `squareSize`, which rebuilds the handlers), and it has to
     survive them holding the values it started from. */
  const gestureRef = useRef(null)
  const longPressTimerRef = useRef(null)

  const imagePadding = squareSize * 0.2
  const imageSize = squareSize * 0.6

  useEffect(() => {
    const canvas = canvasRef.current
    styleCanvas(canvas, allImages.current)
    resizeCanvas(canvas)
    // The initial centring was computed from the window, before the canvas had a box of its own.
    // Redo it against the box it actually got, or the map opens off-centre.
    if (fitToParent) {
      const [centredX, centredY] = clampOffset(
        initialPosition[0] * initialSquareSize - viewWidth(canvas) / 2,
        initialPosition[1] * initialSquareSize - viewHeight(canvas) / 2,
        canvas,
        initialSquareSize
      )
      setOffsetX(centredX)
      setOffsetY(centredY)
    }
    // It's important not to put anything here to avoid re-renders because of draw. We don't care.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* The drawing buffer used to be resized on mount and on `window.resize`, and nothing else. The
     lobby's map box, though, is sized by its grid: it changes when the setup panel below it grows
     (a side picked, a class picked, a player arriving) and again when the first layout settles
     after the fonts land — none of which is a window resize. The buffer stayed at its old size
     while CSS kept the element filling the box, so the map was quietly scaled, and every click
     landed on the wrong cell.

     Watching the parent is what makes those two agree. `cellFromPointer` no longer depends on it
     being true, so this is now about the map being sharp rather than about it being clickable. */
  useEffect(() => {
    const canvas = canvasRef.current
    const parent = canvas?.parentElement
    if (!parent || typeof ResizeObserver === "undefined") return undefined

    const observer = new ResizeObserver(() => {
      const [width, height] = fitToParent ? getParentDimensions(canvas) : getCanvasDimensions()
      if (viewWidth(canvas) === Math.round(width) && viewHeight(canvas) === Math.round(height)) return
      resizeCanvas(canvas)
      // Setting either dimension clears the buffer, so a redraw has to follow. Bumping state
      // rather than calling `draw` here: this callback is built once and would otherwise paint
      // with the offset and zoom of the render that created it.
      setResizes((count) => count + 1)
    })

    observer.observe(parent)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    draw(canvas, ctx)

    // As the events functions change because they depend on the canvas and context and some
    // reactive variables, it's important that we clean up the events and rebuild when the
    // dependencies change.
    return setUpEvents(canvas, ctx)
  })

  // Scroll to another position handler from outside component
  useEffect(() => {
    if (previousScrollTo !== scrollTo) {
      setPreviousScrollTo(scrollTo)

      const targetCol = Math.floor((worldCols() * scrollTo[0]) / 100)
      const targetRow = Math.floor((worldRows() * scrollTo[1]) / 100)
      const newOffsetX = targetCol * squareSize - viewWidth(canvasRef.current) / 2
      const newOffsetY = targetRow * squareSize - viewHeight(canvasRef.current) / 2

      const [clampedX1, clampedY1] = clampOffset(newOffsetX, newOffsetY, canvasRef.current, squareSize)
      setOffsetX(clampedX1)
      setOffsetY(clampedY1)
    }
  }, [previousScrollTo, scrollTo, squareSize])

  /* The same camera, travelled (SV12). Held in a ref and refreshed every render so the flight can
     read where it is starting from without the effect depending on the camera — a dependency on
     `offsetX` would restart the flight on its own first frame. */
  const cameraRef = useRef({ offsetX, offsetY, squareSize })
  cameraRef.current = { offsetX, offsetY, squareSize }
  /* The last `seq` flown, in a **ref** and not in state, unlike `scrollTo`'s twin above. That one
     is a single assignment and can afford to set state and run again; this one holds a running
     animation, and a second pass through the effect would fire its cleanup — cancelling the
     flight on its own first frame, leaving the camera a few pixels from where it started. */
  const lastFlightSeq = useRef(flyTo?.seq ?? null)

  useEffect(() => {
    const seq = flyTo?.seq ?? null
    if (seq === lastFlightSeq.current) return undefined
    lastFlightSeq.current = seq
    const canvas = canvasRef.current
    if (!flyTo || !canvas) return undefined

    const place = (camera) => {
      const size = Math.max(minSquareSize, Math.min(maxSquareSize, camera.squareSize))
      const [placedX, placedY] = clampOffset(
        camera.col * size - viewWidth(canvas) / 2,
        camera.row * size - viewHeight(canvas) / 2,
        canvas,
        size
      )
      setOffsetX(placedX)
      setOffsetY(placedY)
      setSquareSize(size)
      setBorderWidth(size < 40 ? 0 : 1)
    }
    const at = cameraRef.current
    const from = {
      col: (at.offsetX + viewWidth(canvas) / 2) / at.squareSize,
      row: (at.offsetY + viewHeight(canvas) / 2) / at.squareSize,
      squareSize: at.squareSize,
    }
    const to = {
      col: Number(flyTo.col),
      row: Number(flyTo.row),
      squareSize: Number(flyTo.squareSize ?? at.squareSize),
    }
    if (!Number.isFinite(to.col) || !Number.isFinite(to.row)) return undefined

    // One frame, and the dwell that follows still runs: what a player asking for less motion is
    // asking not to have is the travel, not the destination.
    if (asksForStillness() || typeof window.requestAnimationFrame !== "function") {
      place(to)
      return undefined
    }

    let frame = null
    const clock = () => (typeof performance === "object" ? performance.now() : Date.now())
    const started = clock()
    const step = () => {
      const progress = (clock() - started) / FLIGHT_MS
      place(flightFrame({ from, to, progress }))
      if (progress < 1) frame = window.requestAnimationFrame(step)
    }
    frame = window.requestAnimationFrame(step)
    // A flight interrupted — by the next newcomer, or by the view unmounting — stops where it is
    // rather than finishing into a camera nobody is asking for any more.
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame)
    }
  }, [flyTo])

  /* The round playing itself (requirement 36, M5): while `motion` is set, a frame loop redraws the
     map with the show at the moment it is at, and stops when the timeline says it is over or the
     prop changes. `draw` is read through a ref — it is rebuilt every render over the camera's
     state, and a loop that closed over one render's copy would paint a pan that never happened.
     The flight's shape: a real frame queue, cancelled on cleanup. Nothing under reduce-motion. */
  const drawRef = useRef(null)
  /* A battle on screen keeps moving (owner, 2026-10-26: the armies lunge, the blows flash): while any
     cell holds one, the map is redrawn a few times a second — fewer than the playback's every frame,
     which takes over while a round plays. Nothing under reduce-motion. */
  const hasBattles = Object.values(elements || {}).some((cellElements) => cellElements?.some?.((el) => el.type === "combat"))
  useEffect(() => {
    const canvas = canvasRef.current
    if (!hasBattles || motion || !canvas || asksForStillness() || typeof window.setInterval !== "function") return undefined
    const timer = window.setInterval(() => {
      const ctx = canvas.getContext("2d")
      if (ctx && drawRef.current) drawRef.current(canvas, ctx)
    }, 1000 / 24)
    return () => window.clearInterval(timer)
  }, [hasBattles, motion])
  useEffect(() => {
    const canvas = canvasRef.current
    if (!motion || !canvas || asksForStillness() || typeof window.requestAnimationFrame !== "function") return undefined
    let frame = null
    const step = () => {
      frame = null
      const ctx = canvas.getContext("2d")
      if (ctx && drawRef.current) drawRef.current(canvas, ctx)
      if (!stateAt(motion.timeline, showTimeAt(motion)).done) frame = window.requestAnimationFrame(step)
    }
    frame = window.requestAnimationFrame(step)
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame)
    }
  }, [motion])

  /* The hook arrays, held in a ref and refreshed every render.

     They are props, and every caller writes them as an array literal — `onMoveHooks={[thing]}` —
     which is a new array on every render. With `onMoveHooks` in the dependency list below, that
     effect ran on every render, and it *sets state in the caller*: the replay's camera hook stores
     the radar position, which re-renders, which builds a new array, which runs the effect again.
     React stopped it with "Maximum update depth exceeded" and the route it was mid-way through
     never committed — the URL changed and the page did not (owner's report, 2026-09-04).

     The Game view was only ever safe by accident: its whole `<Canvas>` lives inside a `useMemo`,
     so the literals are rebuilt rarely. That is a property of the caller, not of this component,
     and the next caller does not inherit it — so the fix belongs here. The effect wants to fire
     when the *camera* moves, which is exactly `[offsetX, offsetY, squareSize]`; who is listening
     is not part of that question. */
  const hooksRef = useRef({})
  hooksRef.current = { onMoveHooks, onClickHooks, onHoverHooks, onHoverEndHooks }

  useEffect(() => {
    const realOffsetX = offsetX + viewWidth(canvasRef.current) / 2
    const realOffsetY = offsetY + viewHeight(canvasRef.current) / 2
    // Where the camera is, in the two languages its readers speak: percentages for the minimap,
    // and the centre cell with the cell size for anything that means to come back to it (`flyTo`).
    const camera = { col: realOffsetX / squareSize, row: realOffsetY / squareSize, squareSize }
    // Stashed on the element the way `viewScale` is, for a reader that holds only the canvas:
    // basic training's hand finds a cell on screen through it (`components/TutorialPointer`).
    // A getter over the offsets, not this frame's answer: the box is resized under a camera that
    // has not moved (the phone's layout settling after the first paint), and a centre worked out
    // against the old width pointed the hand at the wrong cell until the map was dragged.
    stashCamera(canvasRef.current, { offsetX, offsetY, squareSize })
    hooksRef.current.onMoveHooks.forEach((hook) =>
      hook(convertOffsetCoordinatesToPercentage(realOffsetX, realOffsetY, squareSize), camera)
    )
  }, [offsetX, offsetY, squareSize])

  const draw = (canvas, ctx) => {
    const allImagesComplete = Object.values(allImages.current).every((images) => images[0]?.complete)
    if (!allImagesComplete) return

    // The show at this frame (requirement 36), or nothing: the live board as it is.
    const show =
      motion && !asksForStillness()
        ? stateAt(motion.timeline, showTimeAt(motion))
        : null
    const playing = show && !show.done ? show : null

    // One CSS pixel to `viewScale` device pixels, for everything drawn from here down. Re-applied
    // each pass because a resize resets the context.
    const ratio = viewScale(canvas)
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    // The ground is drawn on device pixels: a cell edge that lands between two of them is
    // antialiased, and two such cells side by side leave a seam that read as a grid across the
    // whole sea. Each cell's box is snapped, and its width is the distance to the next snapped
    // edge, so neighbours share an edge exactly. Everything drawn *on* a cell — labels, frames,
    // sprites — keeps the unsnapped x, y and squareSize: a sub-pixel shift there is invisible.
    const snap = (value) => Math.round(value * ratio) / ratio

    const cols = Math.ceil(viewWidth(canvas) / squareSize) + 2
    const rows = Math.ceil(viewHeight(canvas) / squareSize) + 2
    const startCol = Math.floor(offsetX / squareSize)
    const startRow = Math.floor(offsetY / squareSize)
    const world = activeWorld()
    // The standing stacks and the battles, drawn once every cell's ground is down (below the loop).
    const standing = []
    const battles = []

    for (let i = -1; i < cols; i++) {
      for (let j = -1; j < rows; j++) {
        const actualRow = startRow + j
        // `actualCol` is the tile column the pixel sits in (it can be -3 or 204); `realCol` is
        // the cell it shows. Everything looked up by cell uses the real one, everything drawn
        // uses the tile one.
        const actualCol = startCol + i
        const realCol = wrapCol(actualCol)
        const key = `${realCol}-${actualRow}`
        const x = actualCol * squareSize - offsetX
        const y = actualRow * squareSize - offsetY

        // Above and below the map there is space, not sea: the overscroll lets the view reach
        // here, and an ocean no march can enter would be a lie. Black, with a few stars so the
        // eye reads "the world ends here" rather than "the map failed to load".
        if (!isOnMap(realCol, actualRow)) {
          ctx.fillStyle = "#050607"
          ctx.fillRect(x, y, squareSize, squareSize)
          const star = starAt(actualCol, actualRow)
          if (star) {
            const size = star.bright ? Math.max(2, squareSize / 12) : Math.max(1, squareSize / 24)
            ctx.fillStyle = star.bright ? "rgba(228, 233, 225, 0.95)" : "rgba(228, 233, 225, 0.55)"
            ctx.fillRect(x + star.fx * squareSize, y + star.fy * squareSize, size, size)
          }
          continue
        }

        // Fill square. The sea is a cell like any other from here down (owner's S10b,
        // 2026-09-05): it used to `continue` straight after the blue, so a stack standing on
        // water — and the fire over it, the tent, the frame of a selected sea cell — was never
        // drawn. Only what is land's alone stays land's: the coordinates label.
        //
        // The ground is terrain, not a country colour (owner's ask, 2026-09-07 — "less board
        // game"): a flat biome colour from `utils/terrain`, a dashed line where two countries
        // meet, a soft band where the sea meets land, and no grid — the cell shows itself under
        // the pointer, on the selection, along a route, and once the map is zoomed in enough to
        // carry coordinates. A capital wears the city grey. Nothing on the ground is saturated,
        // so the sprites and the team colours are what the eye finds first.
        const code = world.matrix[key]
        const isSea = !code
        // A national capital's cell (scripts/build-city-names.py, from GeoNames PPLC records, per
        // world) wears the city grey so a capital reads at a glance.
        const isCapital = !isSea && world.capitals.has(key)
        const routeCell = elements[key]?.find((el) => el.type === "highlight")
        // The selected cell (owner's R17): the same yellow the route uses, as a frame with a wash
        // rather than a flood, so the city under it stays readable.
        const isSelected = elements[key]?.some((el) => el.type === "selected")

        // Paint the ground, its edges, and the grid — on a snapped box (see `snap` above).
        const box = {
          x: snap(x),
          y: snap(y),
          w: snap(x + squareSize) - snap(x),
          h: snap(y + squareSize) - snap(y),
          col: realCol,
          row: actualRow,
        }
        drawGround(ctx, { ...box, capital: isCapital })
        drawEdges(ctx, { ...box, grid: Boolean(showGrid && borderWidth) })
        if (hoveredCell && hoveredCell.col === realCol && hoveredCell.row === actualRow) {
          drawHover(ctx, box)
        }
        if (routeCell) {
          // The route being drawn: one outline round all its cells, no fill (owner, 2026-10-26) —
          // each cell draws the sides that face off the route — in the selected cell's yellow
          // (owner, the same day: "the same style as the one we have when we click a cell").
          const edges = routeCell.edges || { top: true, right: true, bottom: true, left: true }
          const inset = 1.5
          ctx.beginPath()
          if (edges.top) {
            ctx.moveTo(x, y + inset)
            ctx.lineTo(x + squareSize, y + inset)
          }
          if (edges.bottom) {
            ctx.moveTo(x, y + squareSize - inset)
            ctx.lineTo(x + squareSize, y + squareSize - inset)
          }
          if (edges.left) {
            ctx.moveTo(x + inset, y)
            ctx.lineTo(x + inset, y + squareSize)
          }
          if (edges.right) {
            ctx.moveTo(x + squareSize - inset, y)
            ctx.lineTo(x + squareSize - inset, y + squareSize)
          }
          ctx.lineWidth = 3
          ctx.strokeStyle = SELECTED_FRAME
          ctx.stroke()
        }
        if (isSelected) {
          ctx.fillStyle = "rgba(255, 255, 0, 0.28)"
          ctx.fillRect(x, y, squareSize, squareSize)
          ctx.lineWidth = 3
          ctx.strokeStyle = SELECTED_FRAME
          ctx.strokeRect(x + 1.5, y + 1.5, squareSize - 3, squareSize - 3)
        }

        // Draw coordinates — only with the grid shown, and never zoomed out (borderWidth is
        // already 0 below the legible size).
        if (showGrid && borderWidth && !isSea) {
          // Dark ink, no halo: every biome is light enough to carry it, and a halo on a light
          // ground read as a smudge in every cell.
          ctx.fillStyle = "rgba(20, 24, 18, 0.7)"
          // "53-41", the notation every modal and report already uses — shorter than "(53,41)".
          const label = key
          // The family is not optional: "12px" alone is an invalid font string, which canvas
          // silently ignores — so the label inherited whatever was set last (the fire emoji's
          // near-cell-sized serif, once any battle was on screen) instead of this size. And the
          // size must actually fit the cell: shrink until the text does, and below legibility
          // draw nothing rather than confetti.
          let fontSize = Math.max(10, squareSize / 5)
          ctx.font = `${fontSize}px monospace`
          const room = squareSize - coordinatesPadding * 2
          const width = ctx.measureText(label).width
          if (width > room) {
            fontSize = Math.floor(fontSize * (room / width))
            ctx.font = `${fontSize}px monospace`
          }
          if (fontSize >= 7) {
            ctx.textAlign = "left"
            ctx.textBaseline = "top"
            ctx.fillText(label, x + coordinatesPadding, y + coordinatesPadding)
          }
        }

        // A ring around a cell, in the colour the caller chose — the lobby uses it to mark the
        // player's own starting cells so the tiny sprite is not the only sign of where they are.
        const outline = elements[key]?.find((el) => el.type === "outline")
        if (outline) {
          ctx.lineWidth = 3
          ctx.strokeStyle = outline.color || "#fff"
          ctx.strokeRect(x + 1.5, y + 1.5, squareSize - 3, squareSize - 3)
        }

        // A battle here: drawn as the two armies fighting it, after the ground (see below the loop),
        // in place of the fire it was until 2026-10-26 (owner, `requirements/36` B1–B4).
        const combat = elements[key]?.find((el) => el.type === "combat")
        if (combat) battles.push({ armies: combat.armies || { human: {}, zombie: {} }, relations: combat.relations, cx: x + squareSize / 2, cy: y + squareSize / 2 })

        // The sprite for this cell: its owner's team decides the default, and their equipped
        // cosmetic overrides it. `skin` is only ever an id the server published, and an
        // unrecognised one falls back rather than drawing nothing.
        const occupant = elements[key]?.find(
          (el) => el.type === "human" || el.type === "zombie"
        )
        if (occupant) {
          const sprite =
            allImages.current[occupant.skin] || allImages.current[`${occupant.type}_default`]
          ctx.drawImage(sprite[0], x + imagePadding, y + imagePadding, imageSize, imageSize)
        }

        // Troops standing on ground nobody owns (requirement 36, S3): a formation of what they
        // are made of, standing, in place of the owner's icon and the tent (owner, 2026-10-26:
        // troops, not an icon). The same group for every player (S5). It wore a ring in the
        // relation colour for a day; the owner took it off (2026-10-23: "I don't like the
        // green/red circles around the troops") — whose it is, the sheet and the tooltip say.
        // Kept for after the loop: a formation is a little wider than its cell, and drawn here the
        // next cell's ground would paint over the tank or the helicopter that reaches into it.
        const stack = elements[key]?.find((el) => el.type === "stack")
        // A stack on a battle cell is one of the armies fighting it, drawn there.
        if (stack && !combat) standing.push({ stack, cx: x + squareSize / 2, cy: y + squareSize / 2 })

        // A camp (owner's R16): troops standing on ground nobody holds. A small tent in the
        // top-right corner, in the campers' side colour, so "mine" and "merely stood on" tell
        // apart at a glance — the crown's corner is the bottom-right, the label's the top-left.
        const camp = elements[key]?.find((el) => el.type === "camp")
        if (camp) {
          const size = Math.max(6, squareSize * 0.28)
          const cx = x + squareSize - size / 2 - 2
          const baseY = y + size + 2
          ctx.beginPath()
          ctx.moveTo(cx - size / 2, baseY)
          ctx.lineTo(cx, baseY - size)
          ctx.lineTo(cx + size / 2, baseY)
          ctx.closePath()
          // The caller says what colour a guest is (see the Game view's builder: mine, ours,
          // theirs). Falling back to the side colour keeps the simpler builders — the replay's —
          // drawing what they always did.
          ctx.fillStyle = camp.color || (camp.team === "zombie" ? TEAM_COLORS.zombie : TEAM_COLORS.human)
          ctx.fill()
          ctx.lineWidth = 1
          ctx.strokeStyle = "rgba(6, 9, 8, 0.9)"
          ctx.stroke()
          // The doorway.
          ctx.beginPath()
          ctx.moveTo(cx - size * 0.16, baseY)
          ctx.lineTo(cx, baseY - size * 0.45)
          ctx.lineTo(cx + size * 0.16, baseY)
          ctx.closePath()
          ctx.fillStyle = "rgba(6, 9, 8, 0.85)"
          ctx.fill()
        }

        // A missile in the air (owner, 2026-10-02): a red crosshair over the square it lands on
        // when the round turns. Drawn as vectors rather than an emoji, for the reason the fire is.
        // It is an order of this board, so it waits for the show, as the march lines do (owner,
        // 2026-10-27: it appeared before the previous round's movements had finished).
        if (!playing && elements[key]?.some((el) => el.type === "strike")) {
          const cx = x + squareSize / 2
          const cy = y + squareSize / 2
          const radius = squareSize * 0.3
          const crosshair = () => {
            ctx.beginPath()
            ctx.arc(cx, cy, radius, 0, 2 * Math.PI)
            ctx.moveTo(cx - radius * 1.45, cy)
            ctx.lineTo(cx - radius * 0.45, cy)
            ctx.moveTo(cx + radius * 0.45, cy)
            ctx.lineTo(cx + radius * 1.45, cy)
            ctx.moveTo(cx, cy - radius * 1.45)
            ctx.lineTo(cx, cy - radius * 0.45)
            ctx.moveTo(cx, cy + radius * 0.45)
            ctx.lineTo(cx, cy + radius * 1.45)
            ctx.stroke()
          }
          ctx.lineWidth = Math.max(3, squareSize * 0.08)
          ctx.strokeStyle = "rgba(6, 9, 8, 0.85)"
          crosshair()
          ctx.lineWidth = Math.max(1.5, squareSize * 0.04)
          ctx.strokeStyle = "#ff3b30"
          crosshair()
        }

        // A tunnel order of this round (owner, 2026-10-05): the tunnel's mouth in the cell's
        // bottom-right corner (the top-right is the camp's tent, the top-left the coordinates, the
        // bottom-left the pick) — beside the crown on a crown city, which owns that corner with an arrow into it (an entry) or out of it (an exit), both when both are
        // ordered. Vectors, as the crosshair is.
        // Waits for the show too, like the crosshair.
        const tunnelOrders = playing ? [] : (elements[key] || []).filter((el) => el.type === "tunnel")
        if (tunnelOrders.length) {
          const size = squareSize * 0.42
          const crownWidth = (elements[key] || []).some((el) => el.type === "crown")
            ? Math.max(7, squareSize * 0.3) + 1.5
            : 0
          const left = x + squareSize * 0.94 - size - crownWidth
          const top = y + squareSize * 0.94 - size
          const cx = left + size / 2
          const mouthY = top + size * 0.78
          ctx.beginPath()
          ctx.ellipse(cx, mouthY, size / 2, size * 0.2, 0, 0, 2 * Math.PI)
          ctx.fillStyle = "rgba(6, 9, 8, 0.92)"
          ctx.fill()
          ctx.lineWidth = Math.max(1, squareSize * 0.03)
          ctx.strokeStyle = "#c58bff"
          ctx.stroke()
          const arrow = (down, shift) => {
            const ax = cx + shift
            const tip = down ? mouthY - size * 0.12 : top
            const tail = down ? top : mouthY - size * 0.12
            const head = size * 0.22 * (down ? -1 : 1)
            ctx.beginPath()
            ctx.moveTo(ax, tail)
            ctx.lineTo(ax, tip)
            ctx.moveTo(ax - Math.abs(head), tip + head)
            ctx.lineTo(ax, tip)
            ctx.lineTo(ax + Math.abs(head), tip + head)
            ctx.lineWidth = Math.max(3, squareSize * 0.08)
            ctx.strokeStyle = "rgba(6, 9, 8, 0.85)"
            ctx.stroke()
            ctx.lineWidth = Math.max(1.5, squareSize * 0.04)
            ctx.strokeStyle = "#c58bff"
            ctx.stroke()
          }
          const loads = tunnelOrders.some((el) => el.direction === "load")
          const unloads = tunnelOrders.some((el) => el.direction === "unload")
          const spread = loads && unloads ? size * 0.2 : 0
          if (loads) arrow(true, -spread)
          if (unloads) arrow(false, spread)
        }

        // A colony ordered this round is not drawn here: the pick at work is laid over the canvas
        // in its bottom-left corner (see `foundingPicks` below), because it swings and the canvas
        // only repaints when something changes.

        // A crown city — one of the up-to-three cells that decide a player's elimination. Marked
        // in gold, faded while its player has lost it (retaking it recovers the crown), and drawn
        // at every zoom: the win condition never drops off the map.
        const crown = elements[key]?.find((el) => el.type === "crown")
        if (crown) {
          drawCrownMark(ctx, x, y, squareSize, crown.held)
        }
      }
    }

    // The standing stacks, over every cell's ground and marks, so none is cut at its cell's edge.
    const battleTime = asksForStillness() ? 0 : (typeof performance === "object" ? performance.now() : Date.now()) / 1000
    battles.forEach(({ armies, relations, cx, cy }) => {
      drawBattle(ctx, { battle: battleFor(armies), cx, cy, cell: squareSize, time: battleTime, relations })
    })
    standing.forEach(({ stack, cx, cy }) => {
      drawFormation(ctx, {
        formation: formationFor(stack.units, stack.team),
        team: stack.team,
        cx,
        cy,
        cell: squareSize,
        angle: stack.angle ?? 0,
        relation: stack.relation,
      })
    })

    // === Range wash: out of the focused cell's reach goes darker, sea included ===
    // What the team already sees (`seenKeys`, U3) is spared the wash too, so the ring around the
    // pressed cell reads against the vision the team has from its other cities, camps and
    // marches — and a city planned here shows only what it would *add*.
    if (focusRange) {
      const { col: fc, row: fr, radius } = focusRange
      ctx.fillStyle = "rgba(6, 9, 8, 0.42)"
      for (let i = -1; i < cols; i++) {
        for (let j = -1; j < rows; j++) {
          const actualRow = startRow + j
          const actualCol = startCol + i
          const realCol = wrapCol(actualCol)
          if (!isOnMap(realCol, actualRow)) continue
          const across = Math.abs(realCol - fc)
          if (Math.min(across, worldCols() - across) + Math.abs(actualRow - fr) <= radius) continue
          if (seenKeys && seenKeys.has(`${realCol}-${actualRow}`)) continue
          ctx.fillRect(actualCol * squareSize - offsetX, actualRow * squareSize - offsetY, squareSize, squareSize)
        }
      }
    }

    // === One round's reach: while a route is drawn, the cells the stack gets to in one round ===
    // A light amber tint and an amber line on the area's outer edge. Under the route and the
    // marches, so the line being drawn stays on top.
    if (reach?.rounds) {
      const inReach = (col, row) => (reach.rounds.get(`${wrapCol(col)}-${row}`) ?? Infinity) <= 1
      ctx.fillStyle = "rgba(232, 168, 56, 0.16)"
      const edges = []
      for (let i = -1; i < cols; i++) {
        for (let j = -1; j < rows; j++) {
          const actualRow = startRow + j
          const actualCol = startCol + i
          if (!isOnMap(wrapCol(actualCol), actualRow) || !inReach(actualCol, actualRow)) continue
          const x = actualCol * squareSize - offsetX
          const y = actualRow * squareSize - offsetY
          ctx.fillRect(x, y, squareSize, squareSize)
          if (!inReach(actualCol, actualRow - 1)) edges.push([x, y, x + squareSize, y])
          if (!inReach(actualCol, actualRow + 1)) edges.push([x, y + squareSize, x + squareSize, y + squareSize])
          if (!inReach(actualCol - 1, actualRow)) edges.push([x, y, x, y + squareSize])
          if (!inReach(actualCol + 1, actualRow)) edges.push([x + squareSize, y, x + squareSize, y + squareSize])
        }
      }
      ctx.strokeStyle = "rgba(232, 168, 56, 0.9)"
      ctx.lineWidth = 2
      ctx.beginPath()
      for (const [x1, y1, x2, y2] of edges) {
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
      }
      ctx.stroke()
    }

    // === Draw movements (arrows + current position highlight) ===
    // The world is a cylinder, so a march is drawn in every tile the view shows: once for the
    // tile the camera is in and once more for each neighbour tile that overlaps the view. The
    // node keys are unwrapped first (198, 199, 200, 201 rather than 198, 199, 0, 1), so a route
    // across the seam is one continuous line and an arrow never spans the world.
    const worldWidth = worldCols() * squareSize
    const visibleTiles = []
    for (let k = -2; k <= 2; k++) {
      const left = k * worldWidth - offsetX
      if (left < viewWidth(canvas) + worldWidth && left + worldWidth > -worldWidth) visibleTiles.push(k)
    }
    const unwrapped = movements.map((movement) => {
      const nodes = unwrapKeys(movement.nodes || [])
      const lift = (key) => {
        const at = (movement.nodes || []).indexOf(key)
        return at >= 0 ? nodes[at] : key
      }
      return { ...movement, nodes, currentPosition: lift(movement.currentPosition) }
    })
    for (const tile of visibleTiles) {
    ctx.save()
    ctx.translate(tile * worldWidth, 0)
    // While a round plays, the board's march lines wait: each moving stack draws its own line, eaten
    // as it walks (below). The route being drawn has no id and stays.
    const drawnRoutes = playing ? unwrapped.filter((movement) => movement.id == null) : unwrapped
    drawnRoutes.forEach(({ type, action, nodes, currentPosition, outOfSight, fromOutOfSight, standsAtEnd, trail }, movementIndex) => {
      // The line is the prototype's (owner, 2026-10-26): thin, dashed and see-through in the
      // relation colour, no casing and no frame on the cells, the corners still turned on our
      // quarter-circles. The round that just turned (owner, 2026-10-05) is lighter again, so it
      // reads as a road already walked under the new round's own.
      ctx.globalAlpha = trail ? ROUTE_ALPHA * 0.6 : ROUTE_ALPHA
      const routeWidth = Math.max(1.5, squareSize * 0.08)
      const routeDash = [squareSize * 0.3, squareSize * 0.25]
      // Two marches on the same road drew on the same pixels and read as one (owner's P24): each
      // movement takes a small deterministic lane offset, so parallel routes sit side by side and
      // their count is countable. The offset scales with the zoom and stays inside the cell.
      const laneStep = Math.max(2, squareSize / 10)
      const laneX = ((movementIndex % 3) - 1) * laneStep
      const laneY = ((Math.floor(movementIndex / 3) % 3) - 1) * laneStep
      // A green route over a green country was invisible (owner's P22): every stroke gets a dark
      // casing first, so the colour reads against any terrain the map can deal.
      const CASING = "rgba(6, 9, 8, 0.8)"
      // Map type to color
      // Theme colours, by relation and not by team: yours green, an ally's blue, an enemy's red,
      // so three armies read apart whichever side you are on (2026-08-21, 2026-09-10). An ally is
      // by definition on your own team, so `ally_human` and `ally_zombie` only say which side the
      // *viewer* is — which is not something the viewer needs telling. The planned route is "self".
      const colors = {
        ally: RELATION_COLORS.ally,
        ally_human: RELATION_COLORS.ally,
        ally_zombie: RELATION_COLORS.ally,
        enemy: RELATION_COLORS.enemy,
        self: RELATION_COLORS.self,
      }
      const color = colors[type] || "black"

      ctx.strokeStyle = color
      ctx.fillStyle = color

      // No trail behind the stack: the road already walked is not in the payload (2026-09-07).

      // --- A one-cell hop (owner's Q11): the two cells' facing edges are one and the same border,
      // so an edge-to-edge line degenerated into a floating arrowhead pointing nowhere. A
      // half-circle over the border says the same thing legibly at any zoom. ---
      const points = nodes.map(parseKey)
      const singleHop =
        points.length === 2 &&
        Math.abs(points[0][0] - points[1][0]) + Math.abs(points[0][1] - points[1][1]) === 1
      if (singleHop && !outOfSight && !fromOutOfSight) {
        // A half-circle between the two cells (owner's R13), standing beside the border rather
        // than on the cells' centres (owner's S3, 2026-09-05): centre to centre it lay over both
        // sprites and hid what it was pointing at. `hopFeet` puts its feet on the two cells' outer
        // edges, so the arc is a full cell wide, bulges away from what the cells hold, and the
        // arrowhead drops into the target along the arc's own tangent.
        const { start, end, outward } = hopFeet({ from: points[0], to: points[1], squareSize, offsetX, offsetY })
        const [fromX, fromY] = [start[0] + laneX, start[1] + laneY]
        const [toX, toY] = [end[0] + laneX, end[1] + laneY]
        const midX = (fromX + toX) / 2
        const midY = (fromY + toY) / 2
        const radius = Math.hypot(toX - fromX, toY - fromY) / 2
        const hopWidth = routeWidth
        const startAngle = Math.atan2(fromY - midY, fromX - midX)
        const endAngle = Math.atan2(toY - midY, toX - midX)
        // Sweep the half that passes through `outward`: canvas angles grow clockwise, so the
        // clockwise sweep from start to end is the right one exactly when the outward point sits
        // a quarter turn past the start in that direction.
        const outwardAngle = Math.atan2(outward[1], outward[0])
        const quarterOn = ((outwardAngle - startAngle) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI)
        const anticlockwise = Math.abs(quarterOn - Math.PI / 2) > 1e-6
        const arrowSize = Math.max(6, Math.min(squareSize * 0.22, radius * 1.4))
        // The dashes stop where the arrowhead begins (owner, 2026-10-26): a last dash ran on under it.
        const gapAngle = Math.min(Math.PI / 3, (arrowSize * 0.9) / radius)
        const lineEnd = anticlockwise ? endAngle + gapAngle : endAngle - gapAngle
        ctx.beginPath()
        ctx.setLineDash(routeDash)
        ctx.arc(midX, midY, radius, startAngle, lineEnd, anticlockwise)
        ctx.lineWidth = hopWidth
        ctx.strokeStyle = color
        ctx.stroke()
        ctx.setLineDash([])
        // The arrowhead: on the target's foot, pointing along the arc's tangent as it arrives —
        // (-sin, cos) of the end angle for a clockwise sweep, the reverse for the other way.
        const turn = anticlockwise ? -1 : 1
        const angle = Math.atan2(turn * Math.cos(endAngle), turn * -Math.sin(endAngle))
        ctx.beginPath()
        ctx.moveTo(toX, toY)
        ctx.lineTo(toX - arrowSize * Math.cos(angle - Math.PI / 6), toY - arrowSize * Math.sin(angle - Math.PI / 6))
        ctx.lineTo(toX - arrowSize * Math.cos(angle + Math.PI / 6), toY - arrowSize * Math.sin(angle + Math.PI / 6))
        ctx.closePath()
        ctx.fill()
      } else if (points.length >= 2) {
        // --- The route as one line, edge to edge (owner's S3, 2026-09-05): it leaves the origin
        // at the edge facing the first corner and enters the destination at the edge facing the
        // last one, and inside each corner cell it turns on a quarter-circle of half a cell —
        // `arcTo` with that radius is tangent to the two legs exactly at the corner cell's edge
        // midpoints, so a turn no longer runs through the corner's centre and over its sprite.
        // Straight runs still cross the cells between two corners; only the endpoints moved. ---
        const first = legEndpoints({ from: points[0], to: points[1], squareSize, offsetX, offsetY })
        const last = legEndpoints({
          from: points[points.length - 2],
          to: points[points.length - 1],
          squareSize,
          offsetX,
          offsetY,
        })
        const centreOf = ([col, row]) => [
          col * squareSize - offsetX + squareSize / 2 + laneX,
          row * squareSize - offsetY + squareSize / 2 + laneY,
        ]
        const startX = first.start[0] + laneX
        const startY = first.start[1] + laneY
        const endX = last.end[0] + laneX
        const endY = last.end[1] + laneY

        // --- The path: dashed and see-through, in the relation colour. No dash under the
        // arrowhead (owner, 2026-10-26): a circle round its tip is cut out of the stroke. Stopping
        // the line short did not do it when the last turn ends on the destination's edge — the
        // straight piece after it has no length, and "short" walked back along the curve. A road
        // into the fog has no arrowhead and runs to its end. ---
        const arrowSize = Math.max(6, squareSize * 0.25)
        const tipGap = outOfSight ? 0 : arrowSize * 0.9
        ctx.save()
        if (tipGap > 0) {
          ctx.beginPath()
          ctx.rect(-1e5, -1e5, 2e5, 2e5)
          ctx.arc(endX, endY, tipGap, 0, Math.PI * 2)
          ctx.clip("evenodd")
        }
        ctx.beginPath()
        ctx.setLineDash(routeDash)
        ctx.moveTo(startX, startY)
        for (let i = 1; i < points.length - 1; i++) {
          const [cornerX, cornerY] = centreOf(points[i])
          const [nextX, nextY] = centreOf(points[i + 1])
          ctx.arcTo(cornerX, cornerY, nextX, nextY, squareSize / 2)
        }
        ctx.lineTo(endX, endY)
        ctx.lineWidth = routeWidth
        ctx.strokeStyle = color
        ctx.stroke()
        ctx.setLineDash([]) // reset
        ctx.restore()

        if (fromOutOfSight) {
          // --- Out of the fog (owner, 2026-10-05): a road whose start the viewer could not see
          // comes in from a cell back, transparent there and whole where sight begins. The line
          // above starts on that border. ---
          const [dx, dy] = first.direction
          const headX = startX - dx * squareSize
          const headY = startY - dy * squareSize
          const rgb = RELATION_RGB[type === "self" ? "self" : type === "enemy" ? "enemy" : "ally"]
          const fade = ctx.createLinearGradient(headX, headY, startX, startY)
          fade.addColorStop(0, `rgba(${rgb.join(",")}, 0)`)
          fade.addColorStop(1, `rgba(${rgb.join(",")}, 1)`)
          const casing = ctx.createLinearGradient(headX, headY, startX, startY)
          casing.addColorStop(0, "rgba(6, 9, 8, 0)")
          casing.addColorStop(1, CASING)
          ctx.beginPath()
          ctx.moveTo(headX, headY)
          ctx.lineTo(startX, startY)
          ctx.lineWidth = routeWidth
          ctx.strokeStyle = fade
          ctx.stroke()
          ctx.strokeStyle = color
        }

        if (outOfSight) {
          // --- Into the fog (owner, 2026-10-05): a road cut where the viewer's sight ended has
          // no destination to point at. It runs on across its last seen square and fades out a
          // cell beyond it, with no arrowhead and no destination frame. ---
          const [dx, dy] = last.direction
          const tailX = endX + dx * squareSize * 2
          const tailY = endY + dy * squareSize * 2
          const rgb = RELATION_RGB[type === "self" ? "self" : type === "enemy" ? "enemy" : "ally"]
          const fade = ctx.createLinearGradient(endX, endY, tailX, tailY)
          fade.addColorStop(0, `rgba(${rgb.join(",")}, 1)`)
          fade.addColorStop(1, `rgba(${rgb.join(",")}, 0)`)
          const casing = ctx.createLinearGradient(endX, endY, tailX, tailY)
          casing.addColorStop(0, CASING)
          casing.addColorStop(1, "rgba(6, 9, 8, 0)")
          ctx.beginPath()
          ctx.moveTo(endX, endY)
          ctx.lineTo(tailX, tailY)
          ctx.lineWidth = routeWidth
          ctx.strokeStyle = fade
          ctx.stroke()
          ctx.strokeStyle = color
        } else {
          // --- Arrowhead at the destination's edge, along the last leg ---
          const angle = Math.atan2(last.direction[1], last.direction[0])
          ctx.beginPath()
          ctx.moveTo(endX, endY)
          ctx.lineTo(endX - arrowSize * Math.cos(angle - Math.PI / 6), endY - arrowSize * Math.sin(angle - Math.PI / 6))
          ctx.lineTo(endX - arrowSize * Math.cos(angle + Math.PI / 6), endY - arrowSize * Math.sin(angle + Math.PI / 6))
          ctx.closePath()
          ctx.fill()
        }
      }

      // No frame on the cells it leaves, passes or reaches (owner, 2026-10-26: "I don't like the
      // cells squared"): the line and its arrowhead say where it goes.
    })
    ctx.globalAlpha = 1
    ctx.restore()
    }

    // === The round playing itself (requirement 36, M5–M6) ===
    // Over the marches — the trail arrows are the roads being walked (M3) — and in every visible
    // tile, as the marches are: each stack of the log at its animated position, which is a
    // fractional cell unwrapped across the seam, so it is wrapped back here and drawn wherever that
    // column shows; then the effects, each from its sheet by progress or as rings and strokes.
    if (playing) {
      const maxCol = worldCols()
      const wrapFraction = (col) => ((col % maxCol) + maxCol) % maxCol
      const boardRoadOf = (id) => unwrapped.find((movement) => movement.id != null && String(movement.id) === String(id))
      /* The line a stack eats as it walks (owner, 2026-10-26: "display the line while units move,
         and keep deleting the line while the troops end up moving"): from where the stack is now,
         along the cells this round still has it walk, then on along the board's road ahead after
         this round — so when the show ends, what is left is exactly the line the board draws. An
         arrowhead wherever the viewer knows where the march goes: the board's road ahead, or the
         round's last cell for a march of the viewer's side and for an enemy seen to land or fight
         there (`endKnown`). Only an enemy that walked out of sight ends with none (owner,
         2026-10-26, game 3 round 88: a march arriving that round had lost its arrow). The dashes
         stay still on the map (`along`) while the line shrinks behind the stack. */
      const drawEatenLine = (sprite) => {
        const road = boardRoadOf(sprite.id)
        const line = eatenLine(sprite, road, maxCol)
        if (!line) return
        const { cells, dx, dy } = line
        const ends = road && (road.nodes || []).length > 1 ? (road.outOfSight ? "fog" : "arrow") : sprite.endKnown ? "arrow" : "cell"
        const centre = ([col, row]) => [col * squareSize - offsetX + squareSize / 2, row * squareSize - offsetY + squareSize / 2]
        const [endCol, endRow] = cells[cells.length - 1]
        const [cx, cy] = centre([endCol, endRow])
        const endX = ends === "arrow" ? cx - (dx * squareSize) / 2 : cx
        const endY = ends === "arrow" ? cy - (dy * squareSize) / 2 : cy
        const color = RELATION_COLORS[sprite.relation] || RELATION_COLORS.enemy
        const arrowSize = Math.max(6, squareSize * 0.25)
        ctx.save()
        ctx.globalAlpha = ROUTE_ALPHA * (sprite.alpha ?? 1)
        if (ends === "arrow") {
          ctx.beginPath()
          ctx.rect(-1e5, -1e5, 2e5, 2e5)
          ctx.arc(endX, endY, arrowSize * 0.9, 0, Math.PI * 2)
          ctx.clip("evenodd")
        }
        ctx.beginPath()
        const [startX, startY] = centre(cells[0])
        ctx.moveTo(startX, startY)
        for (let i = 1; i < cells.length - 1; i++) {
          const [cornerX, cornerY] = centre(cells[i])
          const [nextX, nextY] = centre(cells[i + 1])
          ctx.arcTo(cornerX, cornerY, nextX, nextY, squareSize / 2)
        }
        ctx.lineTo(endX, endY)
        ctx.setLineDash([squareSize * 0.3, squareSize * 0.25])
        ctx.lineDashOffset = (sprite.along || 0) * squareSize
        ctx.lineWidth = Math.max(1.5, squareSize * 0.08)
        ctx.strokeStyle = color
        ctx.stroke()
        ctx.setLineDash([])
        ctx.lineDashOffset = 0
        ctx.restore()
        if (ends === "arrow") {
          ctx.save()
          ctx.globalAlpha = ROUTE_ALPHA * (sprite.alpha ?? 1)
          const angle = Math.atan2(dy, dx)
          ctx.beginPath()
          ctx.moveTo(endX, endY)
          ctx.lineTo(endX - arrowSize * Math.cos(angle - Math.PI / 6), endY - arrowSize * Math.sin(angle - Math.PI / 6))
          ctx.lineTo(endX - arrowSize * Math.cos(angle + Math.PI / 6), endY - arrowSize * Math.sin(angle + Math.PI / 6))
          ctx.closePath()
          ctx.fillStyle = color
          ctx.fill()
          ctx.restore()
        }
      }
      for (const tile of visibleTiles) {
        ctx.save()
        ctx.translate(tile * worldWidth, 0)
        playing.sprites.forEach(drawEatenLine)
        ctx.restore()
      }
      for (const tile of visibleTiles) {
        ctx.save()
        ctx.translate(tile * worldWidth, 0)
        const seconds = (typeof performance === "object" ? performance.now() : Date.now()) / 1000
        // The round's battles, fought until their clash with the stacks standing on them
        // (`stateAt`'s `fights`, owner 2026-10-26), under the stacks still on the move.
        ;(playing.fights || []).forEach((fight) => {
          drawBattle(ctx, {
            battle: battleFor(fight.armies),
            cx: wrapFraction(fight.col) * squareSize - offsetX + squareSize / 2,
            cy: fight.row * squareSize - offsetY + squareSize / 2,
            cell: squareSize,
            time: seconds,
            relations: fight.relations,
          })
        })
        playing.sprites.forEach((sprite) => {
          drawFormation(ctx, {
            formation: formationFor(sprite.units, sprite.team),
            team: sprite.team,
            cx: wrapFraction(sprite.col) * squareSize - offsetX + squareSize / 2,
            cy: sprite.row * squareSize - offsetY + squareSize / 2,
            cell: squareSize,
            angle: sprite.angle,
            time: seconds,
            walking: sprite.walking,
            alpha: sprite.alpha,
            relation: sprite.relation,
          })
        })
        playing.effects.forEach((effect) => {
          const frames = []
          for (let i = 0; i < EFFECT_FRAMES; i += 1) frames.push(allImages.current[effectFrameId(effect.kind, i)]?.[0])
          drawEffect(ctx, {
            kind: effect.kind,
            progress: effect.progress,
            direction: effect.direction,
            frames,
            cx: wrapFraction(effect.col) * squareSize - offsetX + squareSize / 2,
            cy: effect.row * squareSize - offsetY + squareSize / 2,
            size: squareSize * (effect.kind === "blast" ? 1.4 : 1.1),
          })
        })
        ctx.restore()
      }
    }
  }
  drawRef.current = draw

  const resizeCanvas = (canvas) => {
    const [w, h] = fitToParent ? getParentDimensions(canvas) : getCanvasDimensions()
    const ratio = deviceRatio()
    // Setting either dimension clears the buffer *and* resets the context, transform included —
    // which is why `draw` re-applies the scale on every pass rather than once here.
    canvas.width = Math.round(w * ratio)
    canvas.height = Math.round(h * ratio)
    // And the CSS size, in the same breath, because the buffer no longer states it. A canvas with
    // no CSS size falls back to its buffer as its intrinsic size — which was harmless while the
    // two were the same number, and is a map drawn at three times the width of a phone the moment
    // they are not. Only the lobby's stylesheet ever set this; found by running the app rather
    // than by reading it, which is the whole argument for doing so.
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    canvas.viewScale = ratio
  }

  const setUpEvents = (canvas, ctx) => {
    // The wheel zooms, about the pointer (owner, 2026-09-14); the drag is what travels. It is the
    // pinch in one axis — see `wheelZoomFactor` — so the two gestures share one anchor rule and
    // one clamp.
    const handleWheel = (e) => {
      e.preventDefault()
      const factor = wheelZoomFactor(e)
      if (factor === 1) return
      const [bufX, bufY] = toBuffer(e.clientX, e.clientY)
      const {
        squareSize: nextSize,
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      } = pinchTo({
        gesture: {
          startSpan: 1,
          startSquareSize: squareSize,
          anchorCol: (offsetX + bufX) / squareSize,
          anchorRow: (offsetY + bufY) / squareSize,
        },
        span: factor,
        bufferX: bufX,
        bufferY: bufY,
      })
      if (nextSize === squareSize) return

      setSquareSize(nextSize)
      setBorderWidth(nextSize < 40 ? 0 : 1)
      const [clampedX2, clampedY2] = clampOffset(nextOffsetX, nextOffsetY, canvas, nextSize)
      setOffsetX(clampedX2)
      setOffsetY(clampedY2)
    }

    const handleMouseDown = (e) => {
      positionOnMouseDown = [e.clientX, e.clientY]
      isDragging = true
      setStartX(e.clientX)
      setStartY(e.clientY)
      setLastOffsetX(offsetX)
      setLastOffsetY(offsetY)
      canvas.style.cursor = "grabbing"
    }

    // Leaving a cell — to another cell, to the void, or by starting a drag — ends its hover at once
    // so nothing lingers where the pointer no longer is.
    const endHover = () => {
      clearTimeout(hoverTimerRef.current)
      if (lastHoveredCellRef.current) hooksRef.current.onHoverEndHooks.forEach((hook) => hook())
      lastHoveredCellRef.current = null
      setHoveredCell(null)
    }

    const handleMouseMove = (e) => {
      // --- Dragging Logic ---
      if (isDragging) {
        // Clear any existing hover timer if dragging starts
        clearTimeout(hoverTimerRef.current)
        endHover()

        const dx = e.clientX - startX
        const dy = e.clientY - startY
        const newOffsetX = lastOffsetX - dx
        const newOffsetY = lastOffsetY - dy

        const [clampedX3, clampedY3] = clampOffset(newOffsetX, newOffsetY, canvas, squareSize)
        setOffsetX(clampedX3)
        setOffsetY(clampedY3)
        return
      }

      // --- Hover Logic (only runs if not dragging) ---
      const { col, row } = cellFromPointer({
        canvas,
        clientX: e.clientX,
        clientY: e.clientY,
        offsetX,
        offsetY,
        squareSize,
      })

      // If mouse is outside the valid grid, clear timer and do nothing
      if (!isOnMap(col, row)) {
        endHover()
        return
      }

      const lastCell = lastHoveredCellRef.current
      // If the mouse has moved to a new cell, reset the timer
      if (!lastCell || lastCell.col !== col || lastCell.row !== row) {
        endHover()
        lastHoveredCellRef.current = { col, row }
        setHoveredCell({ col, row })

        const { clientX, clientY } = e
        hoverTimerRef.current = setTimeout(() => {
          hooksRef.current.onHoverHooks.forEach((hook) => hook({ col, row, clientX, clientY }))
        }, millisecondsToConsiderHovered)
      }
    }

    const handleMouseUp = (e) => {
      isDragging = false
      canvas.style.cursor = "grab"

      // Only treat as click if no drag. A few pixels of travel is still a click — see TAP_SLOP_PX.
      if (!movedBeyondSlop(positionOnMouseDown[0], positionOnMouseDown[1], e.clientX, e.clientY)) {
        const { col, row } = cellFromPointer({
          canvas,
          clientX: e.clientX,
          clientY: e.clientY,
          offsetX,
          offsetY,
          squareSize,
        })

        if (isOnMap(col, row)) {
          hooksRef.current.onClickHooks.forEach((hook) => hook({ col, row }))
        }
      }
    }

    const handleMouseLeave = () => {
      endHover()
      isDragging = false
      canvas.style.cursor = "grab"
    }

    // --- Touch ------------------------------------------------------------------------------

    const cancelLongPress = () => {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    // Where a client point falls in the map's own coordinates. The buffer and the CSS box are
    // sized separately (see `cellFromPointer`), so a pinch that anchors on raw client pixels
    // drifts off the fingers by exactly that ratio.
    const toBuffer = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect()
      const scaleX = rect.width ? viewWidth(canvas) / rect.width : 1
      const scaleY = rect.height ? viewHeight(canvas) / rect.height : 1
      return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY]
    }

    const beginPan = (touch) => {
      gestureRef.current = {
        mode: "pan",
        startClientX: touch.clientX,
        startClientY: touch.clientY,
        startOffsetX: offsetX,
        startOffsetY: offsetY,
        moved: false,
        inspected: false,
      }
    }

    const beginPinch = (a, b) => {
      const span = touchSpan(a, b)
      // Two fingers down but too close together to price a ratio from. Returning used to leave
      // the *pan* the first finger had already started sitting in the ref, so the map slid under
      // a two-finger hold; dropping the gesture instead means `handleTouchMove` retries this the
      // moment the fingers are far enough apart to mean something.
      if (span < MIN_PINCH_SPAN_PX) {
        gestureRef.current = null
        return
      }
      const [midX, midY] = touchMidpoint(a, b)
      const [bufX, bufY] = toBuffer(midX, midY)
      gestureRef.current = {
        mode: "pinch",
        startSpan: span,
        startSquareSize: squareSize,
        // The world point the fingers opened on, in cells. Holding it under the moving midpoint
        // is what makes the map zoom about the pinch rather than about its own centre.
        anchorCol: (offsetX + bufX) / squareSize,
        anchorRow: (offsetY + bufY) / squareSize,
        moved: true,
        inspected: false,
      }
    }

    const handleTouchStart = (e) => {
      // Also what stops the browser synthesising a second, mouse-shaped copy of this gesture.
      e.preventDefault()
      cancelLongPress()
      // A press-and-hold reading stays up until the next touch, since there is no "moved away".
      endHover()

      if (e.touches.length >= 2) {
        beginPinch(e.touches[0], e.touches[1])
        return
      }

      const touch = e.touches[0]
      beginPan(touch)

      const { clientX, clientY } = touch
      longPressTimerRef.current = setTimeout(() => {
        const gesture = gestureRef.current
        if (!gesture || gesture.mode !== "pan" || gesture.moved) return
        const { col, row } = cellFromPointer({ canvas, clientX, clientY, offsetX, offsetY, squareSize })
        if (!isOnMap(col, row)) return
        // Marked so the lift that follows reads as an inspection rather than a tap: a hold that
        // opened the tooltip must not also select the cell underneath it.
        gesture.inspected = true
        lastHoveredCellRef.current = { col, row }
        setHoveredCell({ col, row })
        hooksRef.current.onHoverHooks.forEach((hook) => hook({ col, row, clientX, clientY }))
      }, LONG_PRESS_MS)
    }

    const handleTouchMove = (e) => {
      // Two fingers on the glass are always a pinch, whatever the ref currently holds: one may
      // have landed after the other, or they may have started too close together to measure. Both
      // used to leave a `pan` in the ref, and a pan reads `touches[0]` — so a two-finger gesture
      // dragged the map instead of zooming it.
      if (e.touches.length >= 2 && gestureRef.current?.mode !== "pinch") {
        e.preventDefault()
        beginPinch(e.touches[0], e.touches[1])
        cancelLongPress()
        return
      }

      const gesture = gestureRef.current
      if (!gesture) return
      e.preventDefault()

      if (gesture.mode === "pinch" && e.touches.length >= 2) {
        const span = touchSpan(e.touches[0], e.touches[1])
        if (span < MIN_PINCH_SPAN_PX) return
        const [midX, midY] = touchMidpoint(e.touches[0], e.touches[1])
        const [bufX, bufY] = toBuffer(midX, midY)
        const {
          squareSize: nextSize,
          offsetX: nextOffsetX,
          offsetY: nextOffsetY,
        } = pinchTo({ gesture, span, bufferX: bufX, bufferY: bufY })

        setSquareSize(nextSize)
        // The same threshold the button zoom uses, so grid lines appear at one size either way.
        setBorderWidth(nextSize < 40 ? 0 : 1)
        const [clampedX4, clampedY4] = clampOffset(nextOffsetX, nextOffsetY, canvas, nextSize)
        setOffsetX(clampedX4)
        setOffsetY(clampedY4)
        return
      }

      if (gesture.mode !== "pan") return
      const touch = e.touches[0]
      if (!touch) return

      if (!gesture.moved && movedBeyondSlop(gesture.startClientX, gesture.startClientY, touch.clientX, touch.clientY)) {
        gesture.moved = true
        cancelLongPress()
      }
      if (!gesture.moved) return

      const nextOffsetX = gesture.startOffsetX - (touch.clientX - gesture.startClientX)
      const nextOffsetY = gesture.startOffsetY - (touch.clientY - gesture.startClientY)
      const [clampedX5, clampedY5] = clampOffset(nextOffsetX, nextOffsetY, canvas, squareSize)
      setOffsetX(clampedX5)
      setOffsetY(clampedY5)
    }

    const handleTouchEnd = (e) => {
      const gesture = gestureRef.current
      cancelLongPress()

      // Lifting one finger of a pinch leaves the other on the glass. Starting a fresh pan from
      // where it now is — rather than carrying on with the pinch's numbers — is what stops the
      // map leaping when somebody finishes a two-finger zoom one finger at a time.
      if (e.touches.length === 1) {
        beginPan(e.touches[0])
        gestureRef.current.moved = true
        return
      }
      if (e.touches.length > 1) return

      gestureRef.current = null
      if (!gesture || gesture.mode !== "pan" || gesture.moved || gesture.inspected) return

      const touch = e.changedTouches[0]
      if (!touch) return
      const { col, row } = cellFromPointer({
        canvas,
        clientX: touch.clientX,
        clientY: touch.clientY,
        offsetX,
        offsetY,
        squareSize,
      })
      if (isOnMap(col, row)) {
        hooksRef.current.onClickHooks.forEach((hook) => hook({ col, row }))
      }
    }

    /* iOS Safari zooms the *page* on a two-finger pinch, and `touch-action: none` does not stop
       it: WebKit answers a pinch with its own `gesture*` events rather than with touchmove alone,
       and it scales the whole document unless those are prevented. So on an iPhone the map's pinch
       appeared to do nothing while the entire UI grew — which is what "zoom does not work on
       mobile" looks like from the outside. Chromium fires none of these, so this costs nothing
       there.

       Bound to the canvas and not to the document on purpose: pinching to enlarge the rest of the
       page is an accessibility feature, and the map is the one element that has its own answer. */
    const handleGesture = (e) => e.preventDefault()

    const handleTouchCancel = () => {
      cancelLongPress()
      gestureRef.current = null
      endHover()
    }

    const resizeAndDraw = () => {
      resizeCanvas(canvas)
      draw(canvas, ctx)
    }

    canvas.addEventListener("wheel", handleWheel)
    canvas.addEventListener("mousedown", handleMouseDown)
    canvas.addEventListener("mousemove", handleMouseMove)
    canvas.addEventListener("mouseup", handleMouseUp)
    canvas.addEventListener("mouseleave", handleMouseLeave)
    // Not passive: every one of these calls preventDefault, both to keep the browser's own pan
    // and zoom out of the way and to stop a tap arriving twice as a synthesised mouse click.
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false })
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false })
    canvas.addEventListener("touchend", handleTouchEnd)
    canvas.addEventListener("touchcancel", handleTouchCancel)
    canvas.addEventListener("gesturestart", handleGesture)
    canvas.addEventListener("gesturechange", handleGesture)
    canvas.addEventListener("gestureend", handleGesture)
    window.addEventListener("resize", resizeAndDraw)

    return () => {
      canvas.removeEventListener("wheel", handleWheel)
      canvas.removeEventListener("mousedown", handleMouseDown)
      canvas.removeEventListener("mousemove", handleMouseMove)
      canvas.removeEventListener("mouseup", handleMouseUp)
      canvas.removeEventListener("mouseleave", handleMouseLeave)
      canvas.removeEventListener("touchstart", handleTouchStart)
      canvas.removeEventListener("touchmove", handleTouchMove)
      canvas.removeEventListener("touchend", handleTouchEnd)
      canvas.removeEventListener("touchcancel", handleTouchCancel)
      canvas.removeEventListener("gesturestart", handleGesture)
      canvas.removeEventListener("gesturechange", handleGesture)
      canvas.removeEventListener("gestureend", handleGesture)
      window.removeEventListener("resize", resizeAndDraw)
    }
  }

  const styleCanvas = (canvas, allImages) => {
    // Initial styling is grab for the map
    canvas.style.cursor = "grab"

    // Load images and draw once they are loaded recursively checking that
    // all of them are loaded
    let drawn = false
    Object.values(allImages).forEach((imgData) => {
      imgData[0].src = imgData[1]
    })
    Object.values(allImages).forEach((imgData) => {
      const landed = () => {
        imgData[2] = true

        if (!drawn && Object.values(allImages).every((imgData) => imgData[2])) {
          drawn = true
          draw(canvas, canvas.getContext("2d"))
        }
      }
      imgData[0].onload = landed
      // A file that is not there is a picture that has finished loading, as far as the first frame
      // is concerned: `drawImage` is never asked for it (`usableImage`), the silhouette is drawn.
      imgData[0].onerror = landed
    })
  }

  /* A colony ordered this round (owner, 2026-10-03: "the same tool and animation as the upgrade in
     bottom left ... small as the tent icon"): the swinging pick of `UnderConstruction`, laid over
     the canvas rather than painted into it — the canvas repaints only when something changes, and
     an animated mark there would mean repainting the whole map every frame. One per place the
     cell is drawn (the world wraps, so a wide view can show a column twice), in the corner the
     label (top left), the tent (top right) and the crown (bottom right) leave free, at the tent's
     size. Re-placed on every render, which is every pan and zoom.

     A discharge ordered this round wears the same pick (owner, 2026-10-23, requirement 36 D5) —
     and the viewer's own is a button: a press on it takes the order back (`onCancelDischarge`).
     One pick a cell, whichever orders it carries. */
  const orderPicks = []
  const canvas = canvasRef.current
  const pickKeys = Object.keys(elements).filter((key) =>
    elements[key]?.some((el) => el.type === "founding" || el.type === "discharge")
  )
  if (canvas && pickKeys.length > 0) {
    const size = Math.max(6, squareSize * 0.28)
    const firstCol = Math.floor(offsetX / squareSize) - 1
    const lastCol = firstCol + Math.ceil(viewWidth(canvas) / squareSize) + 2
    pickKeys.forEach((key) => {
      const [col, row] = key.split("-").map(Number)
      const cancels = Boolean(onCancelDischarge) && elements[key].some((el) => el.type === "discharge" && el.mine)
      for (let actualCol = firstCol; actualCol <= lastCol; actualCol++) {
        if (wrapCol(actualCol) !== col) continue
        orderPicks.push({
          key: `${key}@${actualCol}`,
          left: actualCol * squareSize - offsetX + 2,
          top: row * squareSize - offsetY + squareSize - size * 1.15 - 2,
          size,
          cancel: cancels ? { col, row } : null,
        })
      }
    })
  }

  // The neon 3px frame belonged to the old chrome; the map now meets its panel on a hairline.
  return (
    <>
      <canvas id="canvas" ref={canvasRef} style={{ touchAction: "none", display: "block" }}></canvas>
      {orderPicks.length > 0 && (
        <div className="map-founding-picks">
          {orderPicks.map(({ key, left, top, size, cancel }) =>
            cancel ? (
              <button
                key={key}
                type="button"
                className="map-order-pick-cancel"
                data-action="cancel-discharge"
                data-cell={`${cancel.col}-${cancel.row}`}
                style={{ left, top, fontSize: size }}
                title={cancelDischargeLabel}
                aria-label={cancelDischargeLabel}
                onClick={() => onCancelDischarge(cancel)}
              >
                <UnderConstruction decorative className="map-founding-pick in-button" />
              </button>
            ) : (
              <UnderConstruction
                key={key}
                decorative
                className="map-founding-pick"
                style={{ left, top, fontSize: size }}
              />
            )
          )}
        </div>
      )}
    </>
  )
}

export default Canvas
